import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import {
  useIsMarketComponentLoaded,
  usePrefillDataToast,
} from '@squareup/dex-ui-hooks';
import { ApiExplorerApiPicker } from '@squareup/dex-ui-shared-api-explorer-api-picker';
import { ApiExplorerEndpointPicker } from '@squareup/dex-ui-shared-api-explorer-endpoint-picker';
import {
  useApiExplorerApi,
  useApiExplorerApiResponse,
  useApiExplorerEndpoint,
  useApiExplorerGraph,
  useApiExplorerRequestInitiated,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { AttentionMarker } from '@squareup/dex-ui-shared-attention-marker';
import { Box } from '@squareup/dex-ui-shared-base';
import { MarketButton } from '@squareup/dex-ui-shared-market';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import clsx from 'clsx';
import React, { FC, memo, useMemo, useRef } from 'react';

import { ApiExplorerCopyRequestButton } from './ApiExplorerCopyRequestButton';
import styles from './shared-api-explorer.module.css';

const strings = {
  runButton: {
    large: 'Run request',
    small: 'Run',
  },
};

interface Props {}

const ApiExplorerSelectorContainer: FC<Props> = memo(() => {
  const { isApiRequestRunning } = useApiExplorerApiResponse();
  const onRequestInitiated = useApiExplorerRequestInitiated();
  const endpoint = useApiExplorerEndpoint();
  const api = useApiExplorerApi();

  const graph = useApiExplorerGraph();
  const apis = useMemo(() => {
    // graph.listApis is not stable, therefore we need to memoize
    return graph.listApis();
  }, [graph]);

  const { showAttnMarker } = usePrefillDataToast();

  /*
    This `useParams` section highlights some strong coupling in the API/Endpoint pickers that 
    ultimately affects the portability of API Explorer.
    
    Both pickers will update your route to reflect which API/Endpoint is selected.
    
    In order to do this, the square_version part of the URL is needed. This is different from
    the Square Version in the graph, since it can be "square" or "square-[some-date]".

    This highlights a hidden dependecing between routing and clicked elements in the picker components, 
    and ultimately makes the API less portable.

    Ideally we want to promote using API Explorer in any environment, so eventually we'll need to tackle this.
    For now, it's a problem we are going to live with.
  */
  const params = useParams();
  const currentVersion = params?.['square_version'] as string;

  const ref = useRef<globalThis.HTMLMarketButtonElement>(null);
  const [isMarketComponentLoaded, onMarketRef] = useIsMarketComponentLoaded(
    ref,
    'api-explorer-selectors'
  );

  return (
    <Box
      className={clsx(
        styles['control-container'],
        !isMarketComponentLoaded && styles['hidden']
      )}
    >
      {isMarketComponentLoaded && (
        <>
          <ApiExplorerApiPicker
            apis={apis}
            selectedApi={api}
            currentVersion={currentVersion}
          />
          <ApiExplorerEndpointPicker
            selectedApi={api}
            selectedEndpoint={endpoint}
            currentVersion={currentVersion}
          />
        </>
      )}
      <SpecVersionPickerContainer variant="api-explorer" />
      <ApiExplorerCopyRequestButton ref={onMarketRef} />
      <MarketButton
        {...(isApiRequestRunning && { isLoading: isApiRequestRunning })}
        rank="primary"
        size="small"
        onClick={onRequestInitiated}
        trackingId="api-explorer-run-button-code-block"
        className={styles['run-button']}
      >
        <Box as="span" className={styles['run-large']}>
          {strings.runButton.large}
        </Box>
        <Box as="span" className={styles['run-small']}>
          {strings.runButton.small}
        </Box>
      </MarketButton>
      {showAttnMarker && <AttentionMarker />}
    </Box>
  );
});

ApiExplorerSelectorContainer.displayName = 'ApiExplorerSelectorContainer';

export { ApiExplorerSelectorContainer };
